/**
 * microCMSの画像URLをImgix + WebP形式に最適化するヘルパー関数
 */

export interface ImageOptimizeOptions {
  width?: number;
  height?: number;
  quality?: number;
  fit?: 'clip' | 'crop' | 'scale' | 'max';
  format?: 'webp' | 'auto';
}

/**
 * microCMSの画像URLを最適化
 * @param url - microCMSの画像URL
 * @param options - 画像最適化オプション
 * @returns 最適化された画像URL
 */
export const optimizeImage = (url: string, options: ImageOptimizeOptions = {}): string => {
  if (!url) return '';

  const { width, height, quality = 75, fit = 'max', format = 'webp' } = options;

  const params = new URLSearchParams();

  // WebP形式を指定
  params.append('fm', format);
  params.append('q', quality.toString());

  if (width) {
    params.append('w', width.toString());
  }

  if (height) {
    params.append('h', height.toString());
  }

  params.append('fit', fit);

  // URLにクエリパラメータを追加
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}${params.toString()}`;
};

/**
 * レスポンシブ画像用のsrcset生成
 * @param url - microCMSの画像URL
 * @param widths - 生成する画像幅の配列
 * @param options - 画像最適化オプション
 * @returns srcset文字列
 */
export const generateSrcSet = (
  url: string,
  widths: number[] = [640, 768, 1024, 1280, 1920],
  options: Omit<ImageOptimizeOptions, 'width'> = {},
): string => {
  if (!url) return '';

  return widths
    .map((width) => {
      const optimizedUrl = optimizeImage(url, { ...options, width });
      return `${optimizedUrl} ${width}w`;
    })
    .join(', ');
};

/**
 * 画像URLとalt属性をまとめて返すヘルパー
 * @param url - microCMSの画像URL
 * @param alt - 代替テキスト
 * @param options - 画像最適化オプション
 * @returns 最適化された画像情報
 */
export const getOptimizedImageProps = (
  url: string,
  alt: string = '',
  options: ImageOptimizeOptions = {},
) => {
  return {
    src: optimizeImage(url, options),
    srcSet: generateSrcSet(url, undefined, options),
    alt,
    loading: 'lazy' as const,
  };
};
